import { OnExportReadyCallback } from '@ecosystem/export-utils';
import { CreateInsightDataFn } from '@ecosystem/newron-client-collections';
import { ExportFormat, useAnalytics } from '@ecosystem/newron-client-lib';
import { containers } from '@ecosystem/newron-design-system';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { KpiTextForTracking, PerformanceType } from '@const/PerformanceType';
import ShareComparisonChart from '../../data-visualization/share-comparison-chart/ShareComparisonChart';
import { ShareComparisonChartResp } from '@data-visualization/share-comparison-chart/Types';
import { PERFORMANCE_TREND_TITLE } from '@pages/Utilities';
import RetailerOverviewHeaderIcon from '../../pages/overview/header-icon/RetailerOverviewHeaderIcon';
import { RetailerClientStateKeys } from '@state/reducers/Reducers';
import { constructRevenueTitle } from '@utils/currencyUtils/CurrencyUtils';
import { DEFAULT_VOLUME } from '@utils/factVolumeUtils/FactVolumeUtils';
import {
  CHART_RADIO_BUTTON_TRACKING_TITLE,
  ChartElementName,
  ChartElementType,
  chartInteractionTrackingFunction,
  ChartInteractionTrackingFunctionParam,
} from '@utils/page-analytics/model/ChartInteractionTracking';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { FactRevenue, getPageTypeWithPageName } from '@utils/pageConfig/Types';
import { RetailerOverviewCombinedChartInsight } from '../collections/retailer-overview-combined-chart/Model';
import { Periodicity } from '../time-period-selector/PeriodsTypes';
import { CollectionInsightType } from '../collections/Model';
import { WithHiddenListBox } from '@components/design';
import { GeneralViewState } from '@models/SavedViews';

export interface ShareComparisonCardProps {
  onShareRadioChange: Dispatch<SetStateAction<PerformanceType>>;
  data: ShareComparisonChartResp;
  selectedPeriodicity: Periodicity;
  selectedPerformanceType: string;
  factRevenue: FactRevenue;
  isLoading: boolean;
  isRetailerOverview: boolean;
  onChartDownload?: (exportFormats: ExportFormat[]) => Promise<void>;
  onExportReady?: OnExportReadyCallback;
  chartName?: string;
  componentName?: string;
  volume?: string;
  retailerClientStateKey?: RetailerClientStateKeys;
  testId?: string;
  createInsightDataFn?: CreateInsightDataFn<RetailerOverviewCombinedChartInsight>;
  excludeRadio?: boolean;
  isUsingCollection?: boolean;
  overviewState: GeneralViewState;
  isApiError?: boolean;
}

const ShareComparisonCard: React.FC<ShareComparisonCardProps> = ({
  onShareRadioChange,
  data,
  selectedPeriodicity,
  selectedPerformanceType,
  isLoading,
  onExportReady,
  factRevenue,
  chartName = PERFORMANCE_TREND_TITLE,
  componentName = PERFORMANCE_TREND_TITLE,
  volume = DEFAULT_VOLUME,
  retailerClientStateKey = 'retailerClient',
  testId = 'ShareComparisonCard',
  onChartDownload = async () => {},
  isRetailerOverview,
  excludeRadio = false,
  isUsingCollection,
  createInsightDataFn,
  overviewState,
  isApiError,
}) => {
  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const retailerClientState = useSelector((state: any) => state[retailerClientStateKey]);
  const { track } = useAnalytics();
  const pagesConfig = usePageConfig();
  const createViewState = useCallback(() => overviewState, [overviewState]);

  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);

  const onChange = ({ value }) => {
    //Start Chart Interaction Tracking
    const trackingParam: ChartInteractionTrackingFunctionParam = {
      eventTitle: CHART_RADIO_BUTTON_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState: retailerClientState,
      track: track,
      pageType: getPageTypeWithPageName(retailerClientState.pageViewing),
      pageName: retailerClientState.pageViewing,
      elementName: ChartElementName.SHOW,
      elementType: ChartElementType.RADIO,
      optionChosen: KpiTextForTracking(volume)[value as PerformanceType],
      chartName: chartName,
      access: pagesConfig.access,
    };

    chartInteractionTrackingFunction(trackingParam);
    //End Chart Interaction Tracking

    onShareRadioChange(value as PerformanceType);
  };

  const { Card } = containers;

  const options = [
    { label: constructRevenueTitle(factRevenue), value: PerformanceType.Value },
    { label: volume, value: PerformanceType.Volume },
  ];

  const findLabelOptionsByValue = (value: string) => {
    const option = options.find((opt) => opt.value === value);
    return option ? option.label : constructRevenueTitle(factRevenue);
  };

  const memoizedSelectedLabelOption = useMemo(
    () => findLabelOptionsByValue(selectedPerformanceType),
    [selectedPerformanceType, options],
  );

  return (
    <Card testId={PERFORMANCE_TREND_TITLE}>
      <Card.Title title={PERFORMANCE_TREND_TITLE}>
        <Card.Title.Actions>
          {isRetailerOverview && createInsightDataFn && (
            <RetailerOverviewHeaderIcon
              isDisabled={isLoading || isButtonDisabled} //NOSONAR typescript:S6606
              isPerformanceTrend={true}
              onChartDownload={onChartDownload}
              volume={volume}
              createInsightDataFn={createInsightDataFn}
              collectionInsightType={CollectionInsightType.RetailerOverviewCombinedChart}
              createViewStateFn={createViewState}
            />
          )}
        </Card.Title.Actions>
      </Card.Title>
      <Card.Content>
        <div data-test-id={testId}>
          {!excludeRadio && (
            <div className={'flex items-center pb-4'}>
              <div>
                <WithHiddenListBox
                  label="Show"
                  value={memoizedSelectedLabelOption}
                  options={options}
                  onChange={onChange}
                  isDisabled={isLoading}
                />
              </div>
            </div>
          )}
          <ShareComparisonChart
            isLoading={isLoading}
            data={data}
            isValue={selectedPerformanceType === PerformanceType.Value}
            factRevenue={factRevenue}
            selectedPeriodicity={selectedPeriodicity}
            componentName={componentName}
            volume={volume}
            retailerClientStateKey={retailerClientStateKey}
            onExportReady={onExportReady}
            isUsingCollection={isUsingCollection}
            setIsButtonDisabled={setIsButtonDisabled}
            isApiError={isApiError}
          />
        </div>
      </Card.Content>
    </Card>
  );
};

export default ShareComparisonCard;
